import { useEffect, useMemo, useRef, useState } from "react";
import type { Lineups } from "../../../../../../../types/teams.types";
import { getCaretCoordinates } from "./get-caret-coordinates";
import {
	buildMentionSections,
	getMentionMatch,
	type Mentionable,
	type MentionSection,
} from "./mentionables";

const MENU_WIDTH = 272;
const MENU_GAP = 8;

export interface MentionMenuPosition {
	left: number;
	bottom: number;
}

interface UseMentionAutocompleteArgs {
	value: string;
	onChange: (value: string) => void;
	lineups: Lineups | null;
}

export const useMentionAutocomplete = ({
	value,
	onChange,
	lineups,
}: UseMentionAutocompleteArgs) => {
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [mentionStart, setMentionStart] = useState<number | null>(null);
	const [query, setQuery] = useState("");
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [position, setPosition] = useState<MentionMenuPosition | null>(null);

	const allSections = useMemo(() => buildMentionSections(lineups), [lineups]);

	const sections = useMemo((): MentionSection[] => {
		if (mentionStart === null) return [];

		const q = query.toLowerCase();
		return allSections
			.map((section) => ({
				...section,
				items: section.items.filter(
					(m) =>
						m.fullName.toLowerCase().includes(q) ||
						(m.number !== undefined && String(m.number).startsWith(q)),
				),
			}))
			.filter((section) => section.items.length > 0);
	}, [allSections, mentionStart, query]);

	const flatItems = useMemo(
		() => sections.flatMap((section) => section.items),
		[sections],
	);

	const isOpen = mentionStart !== null && flatItems.length > 0;

	useEffect(() => {
		setSelectedIndex(0);
	}, [query, mentionStart]);

	const syncFromCursor = (cursorPos: number) => {
		const match = getMentionMatch(value, cursorPos);
		if (!match) {
			setMentionStart(null);
			setPosition(null);
			return;
		}

		setMentionStart(match.start);
		setQuery(match.query);

		const textarea = textareaRef.current;
		if (!textarea) return;

		const caret = getCaretCoordinates(textarea, match.start);
		const rect = textarea.getBoundingClientRect();

		setPosition({
			left:
				rect.left +
				Math.min(caret.left, Math.max(0, textarea.clientWidth - MENU_WIDTH)),
			bottom: window.innerHeight - (rect.top + caret.top) + MENU_GAP,
		});
	};

	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		onChange(e.target.value);
		syncFromCursor(e.target.selectionStart ?? e.target.value.length);
	};

	const handleSelectionChange = (
		e: React.SyntheticEvent<HTMLTextAreaElement>,
	) => {
		syncFromCursor(e.currentTarget.selectionStart ?? value.length);
	};

	const close = () => {
		setMentionStart(null);
		setPosition(null);
	};

	const confirm = (mentionable: Mentionable) => {
		if (mentionStart === null) return;

		const textarea = textareaRef.current;
		const cursorPos = textarea?.selectionStart ?? value.length;
		const before = value.slice(0, mentionStart);
		const after = value.slice(cursorPos);
		const insertion = `${mentionable.lastName} `;

		onChange(`${before}${insertion}${after}`);
		close();

		requestAnimationFrame(() => {
			const caretPos = before.length + insertion.length;
			textarea?.focus();
			textarea?.setSelectionRange(caretPos, caretPos);
		});
	};

	const moveSelection = (delta: number) => {
		setSelectedIndex((prev) => {
			const count = flatItems.length;
			return (prev + delta + count) % count;
		});
	};

	const handleKeyDown = (
		e: React.KeyboardEvent<HTMLTextAreaElement>,
	): boolean => {
		if (!isOpen) return false;

		if (e.key === "ArrowDown") {
			e.preventDefault();
			moveSelection(1);
			return true;
		}

		if (e.key === "ArrowUp") {
			e.preventDefault();
			moveSelection(-1);
			return true;
		}

		if (e.key === "Enter" || e.key === "Tab") {
			e.preventDefault();
			const selected = flatItems[selectedIndex];
			if (selected) confirm(selected);
			return true;
		}

		if (e.key === "Escape") {
			e.preventDefault();
			close();
			return true;
		}

		return false;
	};

	return {
		textareaRef,
		isOpen,
		sections,
		flatItems,
		selectedIndex,
		position,
		handleChange,
		handleSelectionChange,
		handleKeyDown,
		selectIndex: setSelectedIndex,
		confirm,
		close,
	};
};
